import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { t, pick } from "@/i18n";
import { BookOpen, Dumbbell, NotebookPen, Target, ArrowRight, Eye, Heart, Sparkles, Hand, Flame, Sun, Coins, Crown, Award, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { awardPoints } from "@/lib/points";
import DailyPractice from "@/components/DailyPractice";
import ProgressRings from "@/components/ProgressRings";
import Leaderboard from "@/components/Leaderboard";

const MILESTONES = [3, 7, 14, 30, 100];

const ICONS = { eye: Eye, heart: Heart, sparkles: Sparkles, hand: Hand };

export default function Dashboard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [progress, setProgress] = useState({ steps_done: [], exercises_done: [], correct_count: 0, total_answered: 0, streak: 0 });
  const [steps, setSteps] = useState([]);
  const [journalCount, setJournalCount] = useState(0);
  const [daily, setDaily] = useState(null);

  const loadDashboard = useCallback(async () => {
    try {
      const r = await api.post("/progress/ping");
      const data = r.data;
      setProgress(data);
      awardPoints(data);
      // Milestone celebration — fires only when today's ping was the one that unlocked a streak day
      if (data.points_earned === 5 && MILESTONES.includes(data.streak)) {
        toast.success(t(lang, `streak_milestone_${data.streak}`), { duration: 5000 });
      }
    } catch (e) {
      console.warn("progress/ping failed, falling back to /progress:", e?.message || e);
      try {
        const r = await api.get("/progress");
        setProgress(r.data);
      } catch (e2) {
        console.error("progress fetch failed", e2);
      }
    }
    try {
      const [stepsRes, journalRes, dailyRes] = await Promise.all([
        api.get("/content/steps"),
        api.get("/journal"),
        api.get(`/content/daily?language=${lang}`),
      ]);
      setSteps(stepsRes.data);
      setJournalCount(journalRes.data.length);
      setDaily(dailyRes.data);
    } catch (e) {
      console.error("dashboard content fetch failed", e);
    }
  }, [lang]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const stepsDone = progress.steps_done?.length || 0;
  const totalSteps = steps.length || 4;
  const accuracy = progress.total_answered ? Math.round((progress.correct_count / progress.total_answered) * 100) : 0;
  const streak = progress.streak || 0;
  const nextStep = steps.find((s) => !progress.steps_done?.includes(s.id)) || steps[0];

  const stats = [
    { icon: Flame, label: t(lang, "streak"), value: `${streak} ${streak === 1 ? t(lang, "day") : t(lang, "days")}`, accent: streak > 0 },
    { icon: Trophy, label: "XP", value: user?.xp ?? 0 },
    { icon: BookOpen, label: t(lang, "steps_completed"), value: `${stepsDone}/${totalSteps}` },
    { icon: Dumbbell, label: t(lang, "exercises_solved"), value: progress.exercises_done?.length || 0 },
    { icon: NotebookPen, label: t(lang, "journal_entries"), value: journalCount },
    { icon: Target, label: t(lang, "accuracy"), value: `${accuracy}%` },
  ];

  const isPremium = user?.plan === "premium";

  return (
    <div className="space-y-12">
      <div>
        <div className="text-xs uppercase tracking-widest text-accent font-medium mb-3">{t(lang, "dashboard")}</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-light" data-testid="dashboard-welcome">
          {t(lang, "welcome")}, {user?.name}.
        </h1>
      </div>

      {/* Level + Badges card */}
      {user && (() => {
        const currentXP = user.xp ?? 0;
        const level = user.level ?? 1;
        const nextXP = user.level_next_xp ?? 50;
        const prevXP = level > 1 ? [0,50,150,300,500,800,1200,1800,2600,3700,5200,7500][level-1] || 0 : 0;
        const range = Math.max(1, nextXP - prevXP);
        const progressPct = Math.min(100, Math.round(((currentXP - prevXP) / range) * 100));
        const badges = user.badges || [];
        const BADGE_META = {
          first_step: { emoji: "🌱", label: "First step" },
          streak_7: { emoji: "🔥", label: "7-day streak" },
          streak_30: { emoji: "🏔️", label: "30-day streak" },
          first_chat: { emoji: "🐒", label: "First Bonobo chat" },
          first_journal: { emoji: "📔", label: "First journal" },
          mediator: { emoji: "🕊️", label: "Mediator (10 chats)" },
          empath_heart: { emoji: "💛", label: "Empath heart (level 5)" },
          peace_bringer: { emoji: "🌍", label: "Peace bringer (3 referrals)" },
        };
        return (
          <div className="p-6 rounded-2xl border border-border bg-card" data-testid="level-card">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="w-12 h-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Award className="w-6 h-6" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground">Level {level}</span>
                  <span className="font-heading text-xl font-medium" data-testid="level-name">{user.level_name}</span>
                </div>
                <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
                  <div
                    className="h-full bg-accent transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                    data-testid="xp-bar"
                  />
                </div>
                <div className="mt-1.5 text-xs text-muted-foreground" data-testid="xp-text">
                  {currentXP} / {nextXP} XP
                </div>
              </div>
            </div>
            {badges.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Badges</div>
                <div className="flex flex-wrap gap-2" data-testid="badges-list">
                  {badges.map((b) => {
                    const m = BADGE_META[b] || { emoji: "🏅", label: b };
                    return (
                      <span
                        key={b}
                        title={m.label}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary text-sm"
                        data-testid={`badge-${b}`}
                      >
                        <span className="text-base">{m.emoji}</span>
                        <span className="text-xs text-muted-foreground">{m.label}</span>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Fase 2: Daily practice hub — 4 cards */}
      <DailyPractice />

      {/* Fase 2: 5 progress rings by life area */}
      <ProgressRings />

      {/* Fase 5: Friends leaderboard */}
      <Leaderboard />

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {stats.map((s) => {
          const isStreak = s.icon === Flame;
          return (
            <div key={s.label} className={`p-5 rounded-xl border border-border bg-card ${s.accent ? "ring-2 ring-accent/40" : ""}`} data-testid={isStreak ? "streak-card" : undefined}>
              {isStreak && s.accent ? (
                <motion.div
                  animate={{ rotate: [0, -8, 8, -6, 6, 0], scale: [1, 1.15, 1, 1.1, 1] }}
                  transition={{ duration: 1.4, repeat: Infinity, repeatDelay: 2 }}
                  className="mb-4"
                >
                  <Flame className="w-5 h-5 text-accent" strokeWidth={1.5} />
                </motion.div>
              ) : (
                <s.icon className={`w-5 h-5 mb-4 ${s.accent ? "text-accent" : "text-primary"}`} strokeWidth={1.5} />
              )}
              <div className="text-2xl font-heading font-medium">{s.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {!isPremium && (
        <Link to="/app/plan">
          <div className="p-6 rounded-2xl border border-accent/40 bg-accent/5 flex items-center justify-between gap-4 flex-wrap hover:bg-accent/10 transition-colors" data-testid="dashboard-upgrade-cta">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center">
                <Crown className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-heading text-lg font-medium">{t(lang, "upgrade")}</div>
                <div className="text-sm text-muted-foreground">
                  {(user?.points || 0)} / 500 {t(lang, "points_to_premium").toLowerCase()}
                </div>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-accent" />
          </div>
        </Link>
      )}

      {nextStep && (
        <div className="p-8 rounded-2xl border border-border bg-secondary/50">
          <div className="text-xs uppercase tracking-widest text-accent font-medium">{t(lang, "next_step")}</div>
          <div className="mt-3 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-medium">{pick(nextStep.title, lang)}</h2>
              <p className="text-muted-foreground mt-2 max-w-xl">{pick(nextStep.subtitle, lang)}</p>
            </div>
            <Link to={`/app/study?step=${nextStep.id}`}>
              <Button data-testid="dashboard-next-btn" className="rounded-full gap-2">
                {stepsDone > 0 ? t(lang, "continue_learning") : t(lang, "start_learning")}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="mt-6">
            <Progress value={(stepsDone / totalSteps) * 100} />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-4 gap-4">
        {steps.map((s, i) => {
          const Icon = ICONS[s.icon] || Eye;
          const done = progress.steps_done?.includes(s.id);
          return (
            <Link key={s.id} to={`/app/study?step=${s.id}`} data-testid={`step-card-${s.id}`}>
              <div className="h-full p-6 rounded-xl border border-border bg-card card-lift">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${done ? "bg-primary text-primary-foreground" : "bg-secondary text-primary"}`}>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-heading text-lg font-medium">{pick(s.title, lang)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
